import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  startTestRun, 
  getTestRunStatus, 
  startConfigOptimization, 
  getOptimizationStatus,
  getDefaultTestQueries,
  getConfigOptimizationTemplates
} from '../services/api';
import { 
  PlayIcon, 
  Cog6ToothIcon, 
  ChartBarIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/solid';

const Testing = () => {
  const [activeTab, setActiveTab] = useState('test-runner');
  const [selectedTestType, setSelectedTestType] = useState('classification');
  const [selectedTemplate, setSelectedTemplate] = useState('balanced_optimization');
  const [customQueries, setCustomQueries] = useState([]);
  const [runningTestId, setRunningTestId] = useState(null);
  const [runningOptimizationId, setRunningOptimizationId] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [optimizationResults, setOptimizationResults] = useState(null);

  const queryClient = useQueryClient();

  // Load default test queries
  const { data: defaultQueries } = useQuery({
    queryKey: ['defaultTestQueries'],
    queryFn: getDefaultTestQueries,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Load optimization templates
  const { data: optimizationTemplates } = useQuery({
    queryKey: ['optimizationTemplates'],
    queryFn: getConfigOptimizationTemplates,
    staleTime: 10 * 60 * 1000 // 10 minutes
  });

  // Poll test run status
  const { data: testRunStatus } = useQuery({
    queryKey: ['testRunStatus', runningTestId],
    queryFn: () => getTestRunStatus(runningTestId),
    enabled: !!runningTestId,
    refetchInterval: 2000, // Poll every 2 seconds
    onSuccess: (data) => {
      if (data.status === 'completed' || data.status === 'failed') {
        setTestResults(data);
        setRunningTestId(null);
      }
    }
  });

  // Poll optimization status
  const { data: optimizationStatus } = useQuery({
    queryKey: ['optimizationStatus', runningOptimizationId],
    queryFn: () => getOptimizationStatus(runningOptimizationId),
    enabled: !!runningOptimizationId,
    refetchInterval: 3000, // Poll every 3 seconds
    onSuccess: (data) => {
      if (data.status === 'completed' || data.status === 'failed') {
        setOptimizationResults(data);
        setRunningOptimizationId(null);
      }
    }
  });

  // Start test run mutation
  const startTestMutation = useMutation({
    mutationFn: ({ testType, testQueries, configParams }) => 
      startTestRun(testType, testQueries, configParams),
    onSuccess: (data) => {
      setRunningTestId(data.test_id);
      setTestResults(null);
    }
  });

  // Start optimization mutation
  const startOptimizationMutation = useMutation({
    mutationFn: ({ optimizationParams, testQueries, scoringWeights }) => 
      startConfigOptimization(optimizationParams, testQueries, scoringWeights),
    onSuccess: (data) => {
      setRunningOptimizationId(data.optimization_id);
      setOptimizationResults(null);
    }
  });

  const handleStartTest = () => {
    const queries = customQueries.length > 0 ? customQueries : null;
    startTestMutation.mutate({
      testType: selectedTestType,
      testQueries: queries,
      configParams: null
    });
  };

  const handleStartOptimization = () => {
    const template = optimizationTemplates?.[selectedTemplate];
    if (!template) return;

    const queries = customQueries.length > 0 ? customQueries : null;
    startOptimizationMutation.mutate({
      optimizationParams: template.optimization_params,
      testQueries: queries,
      scoringWeights: template.scoring_weights
    });
  };

  const addCustomQuery = () => {
    setCustomQueries([...customQueries, {
      id: `custom_${Date.now()}`,
      text: '',
      expected_classification: 'UNCLASSIFIED',
      expected_reasoning: '',
      expected_controls: [],
      expected_compartments: []
    }]);
  };

  const updateCustomQuery = (index, field, value) => {
    const updated = [...customQueries];
    updated[index][field] = value;
    setCustomQueries(updated);
  };

  const removeCustomQuery = (index) => {
    setCustomQueries(customQueries.filter((_, i) => i !== index));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const renderTestResults = () => {
    if (!testResults?.results) return null;

    const { classification } = testResults.results;
    if (!classification) return null;

    const { summary, results } = classification;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ChartBarIcon className="w-5 h-5 mr-2" />
          Test Results
        </h3>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm font-medium text-blue-700">Total Tests</div>
            <div className="text-2xl font-bold text-blue-900">{summary.total_tests}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm font-medium text-green-700">Passed</div>
            <div className="text-2xl font-bold text-green-900">{summary.passed_tests}</div>
            <div className="text-xs text-green-600">
              {((summary.passed_tests / summary.total_tests) * 100).toFixed(1)}%
            </div>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="text-sm font-medium text-red-700">Failed</div>
            <div className="text-2xl font-bold text-red-900">{summary.failed_tests}</div>
            <div className="text-xs text-red-600">
              {((summary.failed_tests / summary.total_tests) * 100).toFixed(1)}%
            </div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="text-sm font-medium text-yellow-700">Avg Response</div>
            <div className="text-2xl font-bold text-yellow-900">
              {summary.average_response_time.toFixed(2)}s
            </div>
          </div>
        </div>

        {/* Accuracy Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="text-sm font-medium text-indigo-700">Overall Accuracy</div>
            <div className="text-xl font-bold text-indigo-900">
              {(summary.overall_accuracy * 100).toFixed(1)}%
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-sm font-medium text-purple-700">Classification Accuracy</div>
            <div className="text-xl font-bold text-purple-900">
              {(summary.classification_accuracy * 100).toFixed(1)}%
            </div>
          </div>
          <div className="bg-pink-50 rounded-lg p-4">
            <div className="text-sm font-medium text-pink-700">Controls Accuracy</div>
            <div className="text-xl font-bold text-pink-900">
              {(summary.controls_accuracy * 100).toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Detailed Results Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expected
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actual
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {results.slice(0, 10).map((result, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {result.test_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {result.expected_classification}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {result.actual_classification}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      result.overall_score >= 0.8 ? 'bg-green-100 text-green-800' :
                      result.overall_score >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {(result.overall_score * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      result.llm_grade === 'A' ? 'bg-green-100 text-green-800' :
                      result.llm_grade === 'B' ? 'bg-blue-100 text-blue-800' :
                      result.llm_grade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {result.llm_grade}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {result.response_time.toFixed(2)}s
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {results.length > 10 && (
          <div className="mt-4 text-sm text-gray-500 text-center">
            Showing first 10 of {results.length} results
          </div>
        )}
      </div>
    );
  };

  const renderOptimizationResults = () => {
    if (!optimizationResults?.results) return null;

    const { config_runs, best_config } = optimizationResults.results;
    if (!config_runs || !best_config) return null;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <AdjustmentsHorizontalIcon className="w-5 h-5 mr-2" />
          Optimization Results
        </h3>

        {/* Best Configuration */}
        <div className="bg-green-50 rounded-lg p-6 mb-6">
          <h4 className="text-md font-semibold text-green-900 mb-3">
            Best Configuration (Score: {best_config.optimization_score.toFixed(3)})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h5 className="text-sm font-medium text-green-800 mb-2">Parameters:</h5>
              <pre className="text-xs bg-white rounded p-2 text-green-700">
                {JSON.stringify(best_config.config_params, null, 2)}
              </pre>
            </div>
            <div>
              <h5 className="text-sm font-medium text-green-800 mb-2">Performance:</h5>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Overall Accuracy: {(best_config.test_summary.overall_accuracy * 100).toFixed(1)}%</div>
                <div>Classification: {(best_config.test_summary.classification_accuracy * 100).toFixed(1)}%</div>
                <div>Avg Response: {best_config.test_summary.average_response_time.toFixed(2)}s</div>
                <div>Confidence: {(best_config.test_summary.average_confidence * 100).toFixed(1)}%</div>
              </div>
            </div>
          </div>
        </div>

        {/* All Configuration Results */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Config Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Optimization Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Overall Accuracy
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Response Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Passed Tests
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {config_runs
                .sort((a, b) => b.optimization_score - a.optimization_score)
                .map((run, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {run.config_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      run.optimization_score >= 0.8 ? 'bg-green-100 text-green-800' :
                      run.optimization_score >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {run.optimization_score.toFixed(3)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {(run.test_summary.overall_accuracy * 100).toFixed(1)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {run.test_summary.average_response_time.toFixed(2)}s
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {run.test_summary.passed_tests}/{run.test_summary.total_tests}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">System Testing & Optimization</h1>
            <p className="mt-1 text-sm text-gray-600">
              Run comprehensive tests and optimize system configuration for peak performance
            </p>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('test-runner')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'test-runner'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <PlayIcon className="w-5 h-5 inline mr-2" />
                Test Runner
              </button>
              <button
                onClick={() => setActiveTab('config-optimizer')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'config-optimizer'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Cog6ToothIcon className="w-5 h-5 inline mr-2" />
                Config Optimizer
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'test-runner' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Test Configuration */}
                  <div className="lg:col-span-1">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Test Configuration</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Test Type
                          </label>
                          <select
                            value={selectedTestType}
                            onChange={(e) => setSelectedTestType(e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            <option value="classification">Classification Tests</option>
                            <option value="sanitization">Sanitization Tests</option>
                            <option value="both">Both</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Test Queries
                          </label>
                          <div className="text-sm text-gray-600 mb-2">
                            {defaultQueries?.test_queries ? 
                              `${Object.values(defaultQueries.test_queries).flat().length} default queries available` :
                              'Loading default queries...'
                            }
                          </div>
                          <button
                            onClick={addCustomQuery}
                            className="w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                          >
                            Add Custom Query
                          </button>
                        </div>

                        <button
                          onClick={handleStartTest}
                          disabled={startTestMutation.isLoading || !!runningTestId}
                          className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                          {runningTestId ? (
                            <>
                              <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                              Running Tests...
                            </>
                          ) : (
                            <>
                              <PlayIcon className="w-4 h-4 mr-2" />
                              Start Test Run
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Test Status */}
                    {(runningTestId || testResults) && (
                      <div className="mt-6 bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Test Status</h3>
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(testRunStatus?.status || testResults?.status)}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {testRunStatus?.status || testResults?.status || 'Unknown'}
                            </div>
                            {testRunStatus?.progress?.stage && (
                              <div className="text-xs text-gray-500">
                                {testRunStatus.progress.stage}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Custom Queries */}
                  <div className="lg:col-span-2">
                    {customQueries.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Custom Test Queries</h3>
                        <div className="space-y-4">
                          {customQueries.map((query, index) => (
                            <div key={query.id} className="bg-white rounded-lg p-4 border">
                              <div className="flex justify-between items-start mb-3">
                                <h4 className="text-sm font-medium text-gray-900">Query {index + 1}</h4>
                                <button
                                  onClick={() => removeCustomQuery(index)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <XCircleIcon className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="grid grid-cols-1 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Text
                                  </label>
                                  <textarea
                                    value={query.text}
                                    onChange={(e) => updateCustomQuery(index, 'text', e.target.value)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    rows={2}
                                    placeholder="Enter test query text..."
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Expected Classification
                                    </label>
                                    <select
                                      value={query.expected_classification}
                                      onChange={(e) => updateCustomQuery(index, 'expected_classification', e.target.value)}
                                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    >
                                      <option value="UNCLASSIFIED">UNCLASSIFIED</option>
                                      <option value="CONFIDENTIAL">CONFIDENTIAL</option>
                                      <option value="SECRET">SECRET</option>
                                      <option value="TOP SECRET">TOP SECRET</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Expected Reasoning
                                    </label>
                                    <input
                                      type="text"
                                      value={query.expected_reasoning}
                                      onChange={(e) => updateCustomQuery(index, 'expected_reasoning', e.target.value)}
                                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                      placeholder="Brief reasoning..."
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Test Results */}
                {renderTestResults()}
              </div>
            )}

            {activeTab === 'config-optimizer' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Optimization Configuration */}
                  <div className="lg:col-span-1">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Optimization Configuration</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Optimization Template
                          </label>
                          <select
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            {optimizationTemplates && Object.entries(optimizationTemplates).map(([key, template]) => (
                              <option key={key} value={key}>
                                {template.description}
                              </option>
                            ))}
                          </select>
                        </div>

                        {optimizationTemplates?.[selectedTemplate] && (
                          <div className="text-sm">
                            <div className="text-gray-700 font-medium mb-2">Parameters to optimize:</div>
                            <ul className="text-gray-600 space-y-1">
                              {Object.entries(optimizationTemplates[selectedTemplate].optimization_params).map(([param, values]) => (
                                <li key={param} className="text-xs">
                                  <code className="bg-gray-200 px-1 rounded">{param}</code>: {JSON.stringify(values)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <button
                          onClick={handleStartOptimization}
                          disabled={startOptimizationMutation.isLoading || !!runningOptimizationId}
                          className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                        >
                          {runningOptimizationId ? (
                            <>
                              <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                              Optimizing...
                            </>
                          ) : (
                            <>
                              <Cog6ToothIcon className="w-4 h-4 mr-2" />
                              Start Optimization
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Optimization Status */}
                    {(runningOptimizationId || optimizationResults) && (
                      <div className="mt-6 bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Optimization Status</h3>
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(optimizationStatus?.status || optimizationResults?.status)}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {optimizationStatus?.status || optimizationResults?.status || 'Unknown'}
                            </div>
                            {optimizationStatus?.progress?.stage && (
                              <div className="text-xs text-gray-500">
                                {optimizationStatus.progress.stage}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Template Details */}
                  <div className="lg:col-span-2">
                    {optimizationTemplates?.[selectedTemplate] && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Template Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Optimization Parameters</h4>
                            <pre className="text-xs bg-white rounded p-3 overflow-x-auto">
                              {JSON.stringify(optimizationTemplates[selectedTemplate].optimization_params, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Scoring Weights</h4>
                            <pre className="text-xs bg-white rounded p-3 overflow-x-auto">
                              {JSON.stringify(optimizationTemplates[selectedTemplate].scoring_weights, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Optimization Results */}
                {renderOptimizationResults()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Testing;